import { Navigate, useLocation } from 'react-router';
import { FEED_REACH } from '../../shared/region';
import { buttonClass } from '../components/Button';
import { Icon, type IconName } from '../components/Icon';
import { EmptyBoard, Ridgeline } from '../components/illustrations';
import { Paper } from '../components/Paper';
import { DevSignIn } from '../features/auth/DevSignIn';
import { useSession } from '../features/auth/session';
import styles from '../features/landing/Landing.module.css';
import { SampleNotes } from '../features/landing/SampleNotes';
import { SiteFooter, SiteHeader } from '../features/landing/SiteChrome';
import { SignInCard } from '../features/landing/SignInCard';

const STEPS = [
  { title: 'Sign in with your email', text: "No password to remember. We'll email you a link." },
  {
    title: 'Confirm you live here',
    text: 'Your phone checks its location once, from home. We keep only a rough area and never show it to anyone.',
  },
  {
    title: "See what's happening nearby",
    text: `Notes from neighbors within ${FEED_REACH} of home, newest first.`,
  },
];

const PROMISES: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'check',
    title: 'Verified neighbors only',
    text: 'Everyone confirms they live here before they can read or post.',
  },
  {
    icon: 'pin',
    title: 'Your address stays private',
    text: "Neighbors see your name and your town, never your street. We don't track where you go.",
  },
  {
    icon: 'board',
    title: 'Newest first, always',
    text: 'No algorithm deciding what you see. Every note from nearby, in the order it was posted.',
  },
  {
    icon: 'shield',
    title: 'Looked after locally',
    text: 'Anything that doesn’t belong can be reported, and local moderators review it.',
  },
];

/** The landing page: where yard signs send people. Also the sign-in page. */
export function Welcome() {
  const { session } = useSession();
  const location = useLocation();

  if (session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  return (
    <div className={styles.page}>
      <SiteHeader action={{ label: 'Sign in', href: '#join' }} />

      <main id="main">
        <section className={`${styles.wrap} ${styles.hero}`} aria-labelledby="headline">
          <div className={styles.sign}>
            <h1 id="headline" className={styles.headline}>
              Your neighborhood’s bulletin board.
            </h1>
            <p className={styles.lede}>
              Lost dogs, road work, power outages, a good plumber. Notes from verified neighbors within {FEED_REACH} of
              home.
            </p>
            <p className={styles.area}>
              <Icon name="pin" size={16} />
              For Catalina, SaddleBrooke, Oro Valley, Oracle, San Manuel and the rest of the northern Tucson suburbs.
            </p>
            <div className={styles.ridge}>
              <Ridgeline height={44} />
            </div>
          </div>
          <SignInCard />
        </section>

        <section className={`${styles.wrap} ${styles.section}`} aria-labelledby="how">
          <h2 id="how" className={styles.sectionTitle}>
            How it works
          </h2>
          <ol className={styles.steps}>
            {STEPS.map((step) => (
              <li key={step.title} className={styles.step}>
                <div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepText}>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className={`${styles.wrap} ${styles.section}`} aria-labelledby="examples">
          <h2 id="examples" className={styles.sectionTitle}>
            What neighbors post
          </h2>
          <SampleNotes />
          <p className={styles.samplesNote}>Examples. Your board shows real notes from people around you.</p>
        </section>

        <section className={`${styles.wrap} ${styles.section}`} aria-labelledby="promises">
          <h2 id="promises" className={styles.sectionTitle}>
            Built for neighbors
          </h2>
          <ul className={styles.promises}>
            {PROMISES.map((p) => (
              <li key={p.title}>
                <Paper padded={false} className={styles.promise}>
                  <Icon name={p.icon} />
                  <div>
                    <p className={styles.promiseTitle}>{p.title}</p>
                    <p className={styles.promiseText}>{p.text}</p>
                  </div>
                </Paper>
              </li>
            ))}
          </ul>
        </section>

        <section className={`${styles.wrap} ${styles.closing}`} aria-labelledby="closing">
          <div className={styles.closingArt}>
            <EmptyBoard />
          </div>
          <h2 id="closing" className={styles.closingTitle}>
            Your board is waiting
          </h2>
          <a href="#join" className={buttonClass({ variant: 'primary' })}>
            Join your neighbors
          </a>
        </section>

        {import.meta.env.DEV && (
          <div className={`${styles.wrap} ${styles.dev}`}>
            <DevSignIn />
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
